import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@ntconsult/aluma-react';
import type { File } from '../types';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
import type { PaginatedResponse } from '@common/types/api';

interface ProcessingStatusEvent {
  fileId: string;
  status: File['status'];
  thumbnailKey?: string | null;
  name: string;
  remainingForUser: number;
}

/**
 * Opens a single SSE connection to `/files/processing-events` while any
 * file in the provided list is still being processed.
 *
 * On each event it:
 *  - Updates all `['files', ...]` React Query caches in-place (no refetch)
 *  - Shows a toast notification on READY or FAILED transitions
 *
 * The connection is automatically closed when the server signals that no
 * more files are in-flight (`remainingForUser === 0`), or when all files
 * in the list have left the processing state (component unmount / re-render).
 */
export function useProcessingEvents(files: File[]) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const esRef = useRef<EventSource | null>(null);
  const toastRef = useRef(toast);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const hasProcessing = files.some(
    (f) => f.status === 'processing' || f.status === 'confirmed',
  );

  useEffect(() => {
    if (!hasProcessing) {
      esRef.current?.close();
      esRef.current = null;
      return;
    }

    // Avoid opening a duplicate connection on re-renders while still connected
    if (esRef.current) return;

    const url = `${API_BASE_URL}/files/processing-events`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.addEventListener('processing-status', (raw: MessageEvent) => {
      let data: ProcessingStatusEvent;
      try {
        data = typeof raw.data === 'string' ? JSON.parse(raw.data) : raw.data;
      } catch {
        return;
      }

      // Patch all cached file-list pages that contain this file
      queryClient.setQueriesData<PaginatedResponse<File>>(
        { queryKey: ['files'], exact: false },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((f) =>
              f.id === data.fileId
                ? {
                    ...f,
                    status: data.status,
                    thumbnailKey: data.thumbnailKey ?? f.thumbnailKey,
                  }
                : f,
            ),
          };
        },
      );

      if (data.status === 'ready') {
        toastRef.current.success({
          title: `"${data.name}" foi processado e está pronto!`,
          duration: 6000,
        });
      } else if (data.status === 'failed') {
        toastRef.current.danger({
          title: `Falha ao processar "${data.name}". Tente reprocessar.`,
          duration: 8000,
        });
      }

      if (data.remainingForUser === 0) {
        es.close();
        esRef.current = null;
      }
    });

    es.onerror = () => {
      es.close();
      esRef.current = null;
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [hasProcessing, queryClient]);
}
